import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import { useEntityPage } from 'c-hooks';
import { useEntityData } from 'c-data';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { CampaignCosts } from 'c-main/Components';
import { Menu as MenuIcon } from '@mui/icons-material';
import Sidebar from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar/Sidebar';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import ReactGA from 'react-ga';
import { campaignIcon } from 'c-main/icons';

import type { SystemSearchEntityResource } from 'c-system-search';

const CampaignCostsPagePage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const { id } = useEntityPage();
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const toggleMobileDrawer = useCallback(() => {
        setMobileDrawerOpen(prev => !prev);
    }, []);

    const t = useCommonTranslation();

    return (
        <>
            {isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'grey.300',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'background.paper',
                        }}
                    >
                        <IconButton onClick={toggleMobileDrawer} edge="start" aria-label="menu">
                            <MenuIcon />
                        </IconButton>
                    </Box>
                    <Sidebar
                        id={id}
                        mobileDrawerOpen={mobileDrawerOpen}
                        toggleMobileDrawer={toggleMobileDrawer}
                    />
                    <Box sx={{ flexGrow: 1, px: 2, pt: 2 }}>
                        <CampaignCosts campaign={campaign} />
                    </Box>
                </Box>
            )}
            {!isMobile && (
                <Box sx={{ display: 'flex' }}>
                    <Sidebar id={id} />
                    <Box component="main" sx={{ flexGrow: 1, pt: 2, px: 2 }}>
                        <CampaignCosts campaign={campaign} />
                    </Box>
                </Box>
            )}
        </>
    );
};

const PageBreadcrumbs: SystemSearchEntityResource<Campaign>['renderEntityBreadcrumbs'] = () => (
    <Box display="flex" />
);

const setup: PageEntityConfig<Campaign> = {
    id: 'CampaignCostsPagePage',
    component: CampaignCostsPagePage,
    systemSearch: {
        title: undefined,
        description: 'Modules.Main.Campaigns.Overview.description',
        icon: campaignIcon,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Campaigns.CampaignCostsPage,
        saveAsRecentPage: true,
        prefix: 'SystemSearch.prefixes.campaign',
        prefixDescription: 'SystemSearch.prefixDescriptions.campaign',
        breadcrumbs: [
            {
                label: 'Modules.Main.Campaigns.List.metaTitle',
                link: PostAuthRoutes.Campaigns.CampaignList,
            },
        ],
        nameGen: entity => `${entity?.name}-${entity?.id}`,
        entityName: 'Campaign',
        permissions: [PermissionName.UniledportalCampaignRead],
        renderEntityBreadcrumbs: PageBreadcrumbs,
        defaultIncludes: [
            'agency',
            'market',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'happydemics',
            'additionalContacts',
        ],
    },
};

export default setup;
